// IND-049: casilla de la Hoja de recuento que se escribe como una fila de
// Excel: escribir sustituye, Enter baja, Tab va a la derecha, flechas por la
// columna, Esc deshace. La sugerencia se ve dentro (vacía) o con un punto azul
// (con valor) y solo se escribe con Ctrl+Enter o doble clic.
import { useLayoutEffect, useRef, useState } from 'react';
import type { KeyboardEvent } from 'react';
import { Box } from '@mui/material';

import type { RecountCellState } from '../utils/recountCellSaver';
import { kgDraft } from '../utils/wasteControl';

const AMBER = '#fde68a';
const BLUE = '#67e8f9';

export type CellMove = { rows: number; columns: number };

export default function RecountSheetCell({
  cellId,
  label,
  value,
  format,
  suggestion,
  typed,
  state,
  onCommit,
  onMove,
  onDiscard,
  onRetry,
}: {
  /** «fila:columna» de lo que se ve, para moverse con el teclado. */
  cellId: string;
  label: string;
  value: number | null;
  format: (value: number) => string;
  suggestion: number | null;
  typed: boolean;
  state: RecountCellState | undefined;
  onCommit: (text: string, fromSuggestion: boolean) => void;
  /** false si no hay casilla a la que ir (Tab sale de la tabla). */
  onMove: (move: CellMove) => boolean;
  onDiscard: () => void;
  onRetry: () => void;
}) {
  // En un ref además de en el estado: al pasar a otra casilla, el blur llega
  // antes de que se repinte y no debe guardar dos veces.
  const draftRef = useRef<string | null>(null);
  const [draft, setDraftState] = useState<string | null>(null);
  const [focused, setFocused] = useState(false);
  // Recién entrada en la casilla: la primera tecla sustituye lo que había (Excel).
  const fresh = useRef(false);
  const input = useRef<HTMLInputElement | null>(null);
  // Todo seleccionado al entrar, ya con el valor para escribir: lo tecleado lo sustituye.
  useLayoutEffect(() => {
    if (focused && fresh.current) input.current?.select();
  }, [focused]);
  const setDraft = (next: string | null) => {
    draftRef.current = next;
    setDraftState(next);
  };
  const failed = state && (state.status === 'error' || state.status === 'invalid' || state.status === 'offline');
  const pending = state && state.status !== 'saved';
  const shown = draft ?? (pending ? state.text : value === null ? '' : format(value));
  const otherSuggestion = suggestion !== null && value !== null && suggestion !== value;
  const emptySuggestion = suggestion !== null && value === null && !pending;
  const suggestionText = suggestion === null ? '' : `≈ ${format(suggestion)}`;

  const commit = () => {
    const text = draftRef.current;
    if (text === null) return;
    setDraft(null);
    onCommit(text, false);
  };
  const useSuggestion = () => {
    if (suggestion === null) return;
    setDraft(null);
    onCommit(kgDraft(suggestion), true);
  };
  const keyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    const plain = !event.ctrlKey && !event.metaKey && !event.altKey;
    if (fresh.current && plain && (event.key.length === 1 || event.key === 'Backspace' || event.key === 'Delete')) {
      event.preventDefault();
      fresh.current = false;
      setDraft(event.key.length === 1 ? event.key : '');
      return;
    }
    if (event.key === 'ArrowLeft' || event.key === 'ArrowRight' || event.key === 'Home' || event.key === 'End') fresh.current = false;
    const move = (rows: number, columns: number) => {
      commit();
      if (onMove({ rows, columns }) || event.key !== 'Tab') event.preventDefault();
    };
    if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) {
      event.preventDefault();
      useSuggestion();
    } else if (event.key === 'Enter') move(event.shiftKey ? -1 : 1, 0);
    else if (event.key === 'Tab') move(0, event.shiftKey ? -1 : 1);
    else if (event.key === 'ArrowDown') move(1, 0);
    else if (event.key === 'ArrowUp') move(-1, 0);
    else if (event.key === 'Escape') {
      event.preventDefault();
      setDraft(kgDraft(value));
      onDiscard();
      fresh.current = true;
      event.currentTarget.select();
    }
  };

  return (
    <Box
      title={suggestion !== null && (otherSuggestion || emptySuggestion) ? `Sugerencia ${suggestionText} · Ctrl+Enter o doble clic para usarla` : undefined}
      onClick={(event) => event.stopPropagation()}
      sx={{ position: 'relative', width: '100%', maxWidth: 120, ml: 'auto' }}
    >
      <Box
        component="input"
        ref={input}
        inputMode="decimal"
        data-cell={cellId}
        aria-label={label}
        aria-invalid={failed ? true : undefined}
        value={shown}
        // IND-052: el espacio fino del final deja entera la cursiva (el navegador la corta en el borde del texto).
        placeholder={emptySuggestion ? `${suggestionText}\u202F` : undefined}
        onFocus={(event) => {
          setFocused(true);
          fresh.current = true;
          setDraft(pending ? state.text : kgDraft(value));
          event.currentTarget.select();
        }}
        onChange={(event) => {
          fresh.current = false;
          setDraft(event.target.value);
        }}
        onMouseDown={() => {
          // Con el foco ya puesto, pulsar dentro es para colocar el cursor.
          if (focused) fresh.current = false;
        }}
        onBlur={() => {
          fresh.current = false;
          setFocused(false);
          commit();
        }}
        onKeyDown={keyDown}
        onDoubleClick={emptySuggestion ? useSuggestion : undefined}
        sx={{
          width: '100%',
          height: 30,
          boxSizing: 'border-box',
          pl: 1,
          pr: failed || state?.status === 'saved' || otherSuggestion ? 2.75 : 1,
          borderRadius: 1.5,
          border: '1px solid transparent',
          borderBottomColor: 'rgba(148,163,184,0.35)',
          bgcolor: 'rgba(148,163,184,0.06)',
          color: typed ? AMBER : '#f8fafc',
          font: 'inherit',
          fontSize: 14,
          fontWeight: 700,
          textAlign: 'right',
          fontVariantNumeric: 'tabular-nums',
          outline: 'none',
          '&::placeholder': { color: BLUE, fontStyle: 'italic', fontWeight: 500, opacity: 0.85 },
          '&:focus': { borderColor: '#22d3ee', bgcolor: '#0b1220' },
          ...(failed
            ? {
                borderColor: state.status === 'offline' ? 'rgba(251,191,36,0.7)' : 'rgba(248,113,113,0.7)',
                bgcolor: state.status === 'offline' ? 'rgba(251,191,36,0.08)' : 'rgba(248,113,113,0.10)',
                color: state.status === 'offline' ? AMBER : '#fecaca',
              }
            : {}),
        }}
      />
      {state?.status === 'saved' && !focused ? (
        <Box component="span" aria-label="Guardado" sx={{ position: 'absolute', right: 6, top: 6, fontSize: 12, fontWeight: 800, color: '#86efac', pointerEvents: 'none' }}>
          ✓
        </Box>
      ) : failed ? (
        <Box
          component="button"
          type="button"
          tabIndex={-1}
          title={
            state.status === 'invalid'
              ? 'No es un número válido: corrígelo'
              : state.status === 'offline'
                ? 'Sin conexión: se enviará al volver. Pulsa para reintentar'
                : 'PROA no lo guardó. Pulsa para reintentar'
          }
          onMouseDown={(event) => event.preventDefault()}
          onClick={state.status === 'invalid' ? undefined : onRetry}
          sx={{
            position: 'absolute',
            right: 3,
            top: 5,
            p: 0,
            width: 18,
            height: 20,
            border: 'none',
            bgcolor: 'transparent',
            fontSize: 12,
            cursor: state.status === 'invalid' ? 'default' : 'pointer',
            color: state.status === 'offline' ? AMBER : '#fca5a5',
          }}
        >
          ⚠
        </Box>
      ) : otherSuggestion ? (
        <Box component="span" sx={{ position: 'absolute', right: 7, top: 12, width: 6, height: 6, borderRadius: 999, bgcolor: '#22d3ee', pointerEvents: 'none' }} />
      ) : null}
      {focused && otherSuggestion ? (
        // Con el foco se ve la sugerencia, encima de la fila: no añade altura.
        <Box
          sx={{
            position: 'absolute',
            right: 0,
            top: '100%',
            mt: 0.25,
            zIndex: 2,
            px: 0.75,
            py: 0.25,
            borderRadius: 1,
            bgcolor: '#0b1220',
            border: '1px solid rgba(34,211,238,0.4)',
            color: BLUE,
            fontSize: 11,
            whiteSpace: 'nowrap',
            pointerEvents: 'none',
          }}
        >
          {`${suggestionText} · Ctrl+Enter`}
        </Box>
      ) : null}
    </Box>
  );
}
